// src/Components/AppStorePrompt.jsx
// A small "get the Android app" row for Home, in the page flow (it never floats
// over anything). Shown only to Android visitors in a normal browser tab: not
// inside the installed app, and not after they dismiss it. There is no iOS app.
import { useEffect, useState } from "react";
import { Smartphone, X } from "lucide-react";

const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.simihillary.nameit";
// Same key as the old prompt, so anyone who already dismissed it stays dismissed.
const DISMISSED_KEY = "appStorePromptDismissed";

function shouldShow() {
  try {
    if (localStorage.getItem(DISMISSED_KEY)) return false;
  } catch {
    // Storage can be blocked: show it; dismissing just won't persist.
  }
  if (!/android/i.test(navigator.userAgent || "")) return false;
  return !window.matchMedia("(display-mode: standalone)").matches;
}

const AppStorePrompt = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(shouldShow());
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    setVisible(false);
    try {
      localStorage.setItem(DISMISSED_KEY, "true");
    } catch {
      // Not critical.
    }
  };

  return (
    <div className="flex items-center gap-3 bg-brand-surface border border-brand-border rounded-lg p-3 mb-6 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
      <span className="w-10 h-10 rounded-lg bg-brand-accentMuted text-brand-accent flex items-center justify-center shrink-0" aria-hidden="true">
        <Smartphone size={20} />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-brand-text leading-snug">NameIt! for Android</p>
        <p className="text-xs text-brand-textSecondary leading-snug">Get the app from Google Play.</p>
      </div>
      <a
        href={PLAY_STORE_URL}
        className="shrink-0 bg-brand-accent text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-red-700 active:scale-[0.98] transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-accent"
      >
        Get it
      </a>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="-mr-1 w-8 h-8 shrink-0 flex items-center justify-center rounded-full text-brand-placeholder hover:text-brand-text hover:bg-brand-input transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-accent"
      >
        <X size={16} />
      </button>
    </div>
  );
};

export default AppStorePrompt;
